import type { Sentiment } from '@/components/timeline/SentimentPill';
import { formatTimeISO, groupItemsByDay, type DayGroup } from '@/lib/date';

export type ActivityEvent = {
  id: string;
  kind: 'chat' | 'journal';
  occurredAt: string;
  title: string;
  summary?: string | null;
  url?: string | null;
  sentiment?: Sentiment | null;
  patient?: {
    id: number;
    name: string;
  };
  meta?: {
    messageCount?: number;
    newChat?: boolean;
    visibility?: 'public' | 'private';
    journalDate?: string | null;
  };
};

export type ActivityEventPresentation = {
  tone: 'chat' | 'journal';
  timeLabel: string;
  label: string;
  summary: string;
  privacyLabel: 'Privata' | 'Pubblica' | null;
  isNewChat: boolean;
  messageCount: number | null;
};

export type ActivityDayGroup = DayGroup<ActivityEvent>;

export type ActivityPatientGroup = {
  id: number;
  name: string;
  events: ActivityEvent[];
};

export type ActivityDayGroupWithPatients = ActivityDayGroup & {
  patients: ActivityPatientGroup[];
};

export function groupActivityEvents(
  events: ActivityEvent[],
  options?: { byPatient?: boolean },
): ActivityDayGroup[] | ActivityDayGroupWithPatients[] {
  const groups = groupItemsByDay(events, (event) => event.occurredAt);

  if (!options?.byPatient) return groups;

  return groups.map((group) => {
    const patients = new Map<number, ActivityPatientGroup>();
    group.items.forEach((event) => {
      const patientId = event.patient?.id ?? -1;
      const patientName = event.patient?.name || 'Paziente';
      const bucket = patients.get(patientId) || { id: patientId, name: patientName, events: [] };
      bucket.events.push(event);
      patients.set(patientId, bucket);
    });

    const sortedPatients = Array.from(patients.values()).sort(
      (a, b) => getEventTimestampValue(b.events[0]) - getEventTimestampValue(a.events[0]),
    );

    return {
      ...group,
      patients: sortedPatients,
    };
  });
}

export function getActivityEventPresentation(event: ActivityEvent): ActivityEventPresentation {
  const timeLabel = formatTimeISO(event.occurredAt) || '--:--';
  const messageCount =
    event.kind === 'chat' && typeof event.meta?.messageCount === 'number'
      ? event.meta.messageCount
      : null;
  const isNewChat = event.kind === 'chat' && event.meta?.newChat === true;
  const privacyLabel =
    event.meta?.visibility === 'private'
      ? 'Privata'
      : event.meta?.visibility === 'public'
        ? 'Pubblica'
        : null;

  if (event.kind === 'chat') {
    return {
      tone: 'chat' as const,
      timeLabel,
      label: event.title || 'Chat',
      summary: event.summary || '—',
      privacyLabel,
      isNewChat,
      messageCount,
    };
  }

  return {
    tone: 'journal' as const,
    timeLabel,
    label: event.title || 'Diario',
    summary: event.summary || '—',
    privacyLabel: null,
    isNewChat: false,
    messageCount: null,
  };
}

function getEventTimestampValue(event?: ActivityEvent) {
  if (!event?.occurredAt) return 0;
  const parsed = new Date(event.occurredAt);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}
